import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import * as THREE from "three";
import styles from "./workSlider.module.css";

gsap.registerPlugin(ScrollTrigger);

const vertexShader = `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const fragmentShader = `
uniform sampler2D uTexture1;
uniform sampler2D uTexture2;
uniform float uProgress;
uniform float uDirection;

varying vec2 vUv;

void main() {
    float curve = smoothstep(0.0, 0.0, vUv.y) * pow(1.0 - uProgress, 1.0);
    if (uProgress > 0.01) {
        float wave = sin(vUv.y * 3.14 + uProgress * 1.0) * 0.05 + 0.95;
        curve *= wave;
    }
    float mask = smoothstep(0.0, 0.0, (uDirection > 0.0 ? 1.0 - vUv.x : vUv.x) - curve + uProgress);

    vec4 color1 = texture2D(uTexture1, vUv);
    vec4 color2 = texture2D(uTexture2, vUv);

    gl_FragColor = mix(color1, color2, mask);
}`;

const WorkSlider = ({ imageUrls, link }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [dominantIndex, setDominantIndex] = useState(0); // Tracks which image is visually dominant
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
    const totalImages = imageUrls.length;

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isMobile || !canvasRef.current || !containerRef.current) return;
        let animationFrameId;

        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true });

        const handleResize = () => {
            const { clientWidth: width, clientHeight: height } = containerRef.current;
            renderer.setSize(width, height);
        };
        handleResize();
        window.addEventListener("resize", handleResize);

        const loader = new THREE.TextureLoader();
        const textures = imageUrls.map(url => loader.load(url));

        const geometry = new THREE.PlaneGeometry(2, 2);
        const material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            uniforms: {
                uTexture1: { value: textures[0] },
                uTexture2: { value: textures[1] },
                uProgress: { value: 0 },
                uDirection: { value: 1 },
            },
        });

        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        const animate = () => {
            renderer.render(scene, camera);
            animationFrameId = requestAnimationFrame(animate);
        };
        animate();

        ScrollTrigger.create({
            trigger: containerRef.current,
            start: "top+=-130px top",
            end: () => `+=${window.innerHeight * (totalImages - 1)}`,
            scrub: 1,
            pin: true,
            //markers: true,
            anticipatePin: 1,
            onUpdate: (self) => {
                const progress = self.progress;
                const imageIndex = Math.min(Math.floor(progress * totalImages), totalImages - 1);
                const nextImageIndex = Math.min(imageIndex + 1, totalImages - 1);
                const localProgress = (progress * totalImages) % 1;

                material.uniforms.uTexture1.value = textures[imageIndex];
                material.uniforms.uTexture2.value = textures[nextImageIndex];
                material.uniforms.uProgress.value = localProgress;

                setCurrentIndex(imageIndex);

                // Determine which image is visually dominant (more than 50% visible)
                if (localProgress > 0.5) {
                    setDominantIndex(nextImageIndex);
                } else {
                    setDominantIndex(imageIndex);
                }
            }
        });

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener("resize", handleResize);
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [totalImages, isMobile, imageUrls]);

    const handleCanvasClick = () => {
        console.log("Dominant Index:", dominantIndex);
        if (link[dominantIndex]) {
            window.location.href = link[dominantIndex];
        }
    };

    if (isMobile) {
        return (
            <div className={styles.work_slider_main}>
                <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    navigation
                    pagination={true}
                    loop={true}
                    autoplay={true}
                    className={styles.swiper}
                >
                    {imageUrls.map((image, index) => (
                        <SwiperSlide key={index} className={styles.swiper_slide} onClick={() => window.location.href = link[index]}>
                            <img src={image} alt={`Slide ${index}`} className={styles.swiper_image} loading="lazy" />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        );
    }

    return (
        <div className={styles.work_slider_main} style={{ height: `calc(100dvh * ${totalImages})` }}>
            <div ref={containerRef} className={styles.container} onClick={handleCanvasClick}>
                <canvas ref={canvasRef} className={styles.canvas} />
            </div>
        </div>
    );
};

export default WorkSlider;
